import { Stack, Text } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_FOLLOWUP_DELAY_MS_FOR_NEW_MS,
  FOLLOWUP_DELAY_MINUTES_TO_MS,
  MIN_FOLLOWUP_DELAY_MINUTES,
  MAX_FOLLOWUP_DELAY_MINUTES,
  WELCOME_BEGINNING_MAX_LENGTH,
  WELCOME_CONFIRMATION_MAX_LENGTH,
  WELCOME_ENDING_MAX_LENGTH,
} from '@app/constants/welcome-flow-config';
import {
  removeLastWhitespace,
  removeFirstWhitespace,
} from '@app/helpers/common';
import {
  useAdminConfig,
  useAdminConfigById,
} from '@app/hooks/use-admin-config';
import { NAMESPACE } from '@app/i18n';
import {
  useWelcomeMessageRuleset,
  type WelcomeMessageRule,
} from '@app/services/admin';
import { LayoutAdminMessagePreview } from '@chakra-composites/layout-admin-message-preview';
import { LayoutAdminPage } from '@chakra-composites/layout-admin-page';
import { LayoutAdminPageHeader } from '@chakra-composites/layout-admin-page-header';
import { type MessageItem } from '@chakra-composites/message-preview';
import { TaggedTextarea } from '@chakra-composites/tagged-textarea';
import { Alert } from '@chakra-primitives/alert';
import { Field } from '@chakra-primitives/field';
import {
  NumberInputRoot,
  NumberInputField,
} from '@chakra-primitives/number-input';
import { SegmentedControl } from '@chakra-primitives/segmented-control';
import { Switch } from '@chakra-primitives/switch';
import { toaster } from '@chakra-primitives/toaster';

import { useWelcomeDirection } from './use-welcome-direction';
import buildRule from './welcome-message-rule-helpers';
import {
  saveMultipleRulesets,
  invalidateWelcomeMessageQueries,
} from './welcome-message-save';
import { hydratePrimaryBody } from './welcome-message-secondary';
import {
  cleanRule,
  WELCOME_MESSAGE_TOKENS,
  MOID_LEGAL_MESSAGE,
  placeholderToTokens,
  convertMentionFormatToPlainTokens,
  replaceTokensWithPreviewValues,
  extractTokensWithDuplicates,
  tokenDisplayTransform,
  convertTokensToMentionFormat,
  rulesetTypeFor,
} from './welcome-message-utils';

// Spliced into the body at save time rather than picked by the author, which is
// why it is absent from the shared token list.
const MOID_LEGAL_MESSAGE_TOKEN_ID = 'marleyOptInDisabledLegalMessage';

interface WelcomeMessageMarleyOptInDisabledProps {
  caseType: string;
  lineOfBusinessId?: string;
  orgPrefix?: string;
  adminConfigId?: string;
}

interface WelcomeMessageFormData {
  beginningMessageAssigned: string;
  endingMessageAssigned: string;
  isEndingMessageEnabledAssigned: boolean;
  beginningMessageUnassigned: string;
  endingMessageUnassigned: string;
  isEndingMessageEnabledUnassigned: boolean;
  followupMessage: string;
  isFollowupEnabled: boolean;
  followupDelay: number;
}

const WelcomeMessageMarleyOptInDisabled: React.FC<
  WelcomeMessageMarleyOptInDisabledProps
> = ({ caseType, lineOfBusinessId, orgPrefix, adminConfigId }) => {
  const { t } = useTranslation(NAMESPACE.MESSAGE_TEMPLATES);
  const fallbackConfig = useAdminConfig();
  const configById = useAdminConfigById(adminConfigId || '');
  const config = adminConfigId ? configById : fallbackConfig;

  // Fetch ruleset
  const direction = useWelcomeDirection();
  const lobParam = useMemo(
    () => (lineOfBusinessId ? `?lobId=${lineOfBusinessId}` : ''),
    [lineOfBusinessId],
  );

  const {
    data: messageData,
    isLoading: isLoadingMessage,
    isFetching: isFetchingMessage,
  } = useWelcomeMessageRuleset(
    rulesetTypeFor('WELCOME_FLOW_INITIAL_MESSAGE', direction),
    lobParam,
  );

  const {
    data: followupMessageData,
    isLoading: isLoadingFollowup,
    isFetching: isFetchingFollowup,
  } = useWelcomeMessageRuleset(
    rulesetTypeFor('WELCOME_FLOW_OPTIN_FOLLOWUP', direction),
    lobParam,
  );

  const queryClient = useQueryClient();
  const [isSavingState, setIsSavingState] = useState(false);
  const isLoadingData = isLoadingMessage || isLoadingFollowup;
  const isFetchingData = isFetchingMessage || isFetchingFollowup;

  // Helper function to find rules with marleyOptInDisabled = true
  const findRuleWithLogging = useCallback(
    (
      ruleset: typeof messageData,
      assignment?: 'assigned' | 'unassigned',
    ): WelcomeMessageRule | undefined => {
      const result = ruleset?.rules?.find((r) => {
        const conditions = r.rule.conditions.all;

        const matchesCaseType =
          conditions.find((c) => c.fact === 'caseType')?.value === caseType;
        const matchesMarleyOptIn =
          conditions.find((c) => c.fact === 'marleyOptInDisabled')?.value ===
          true;
        const assignmentValue = assignment === 'assigned';
        const matchesAssignment =
          conditions.find((c) => c.fact === 'caseAssignment')?.value ===
          assignmentValue;

        if (!assignment) {
          return matchesCaseType && matchesMarleyOptIn;
        }

        // For Marley Opt-In Disabled, optStatus is not part of the rule conditions
        return matchesCaseType && matchesMarleyOptIn && matchesAssignment;
      });

      return result;
    },
    [caseType],
  );

  // Parse messages from ruleset
  const savedData = useMemo(() => {
    const assignedRule = findRuleWithLogging(messageData, 'assigned');
    const unassignedRule = findRuleWithLogging(messageData, 'unassigned');
    const followupRule = findRuleWithLogging(followupMessageData, undefined);

    const followupBody = hydratePrimaryBody(followupRule?.messageTemplate);

    // Parse assigned message
    // The legacy token name is in the tokens array, not in the raw body
    const assignedTokens = assignedRule?.messageTemplate?.tokens || [];
    const assignedRawBody = assignedRule?.messageTemplate?.body || '';

    let beginningAssigned = '';
    let endingAssigned = '';

    // First, convert ALL tokens to mention format
    let assignedConverted = assignedRawBody;
    if (assignedTokens.length > 0) {
      assignedConverted = placeholderToTokens(assignedRawBody, assignedTokens);
    }

    // Check if the LAST token is a legacy token with suffix
    // (e.g., marleyOptInDisabledLegalMessageBoo)
    const lastToken = assignedTokens[assignedTokens.length - 1];
    const legacyTokenMatch = lastToken?.token?.match(
      /^marleyOptInDisabledLegalMessage(.+)$/,
    );

    if (legacyTokenMatch) {
      // Legacy token found - extract suffix and remove the token from the message
      const [, suffix] = legacyTokenMatch;
      const legacyTokenPattern = `@[${lastToken.token}](${lastToken.token})`;
      const lastTokenIndex = assignedConverted.lastIndexOf(legacyTokenPattern);

      if (lastTokenIndex !== -1) {
        beginningAssigned = assignedConverted.substring(0, lastTokenIndex);
        endingAssigned = suffix;
      } else {
        beginningAssigned = assignedConverted;
      }
    } else {
      // Standard format - split on legal message token if present
      const legalTokenPattern =
        /@\[marleyOptInDisabledLegalMessage\]\(marleyOptInDisabledLegalMessage\)|\$marleyOptInDisabledLegalMessage/;
      const legalTokenIndex = assignedConverted.search(legalTokenPattern);

      if (legalTokenIndex !== -1) {
        // Legal token found - split the message
        const match = assignedConverted.match(legalTokenPattern);
        if (match) {
          const parts = assignedConverted.split(match[0]);
          beginningAssigned = parts[0];
          endingAssigned = parts.slice(1).join('');
          // Remove any remaining legal token occurrences from beginning
          beginningAssigned = beginningAssigned
            .replace(
              /@\[marleyOptInDisabledLegalMessage\]\(marleyOptInDisabledLegalMessage\)/g,
              '',
            )
            .replace(/\$marleyOptInDisabledLegalMessage/g, '');
        } else {
          beginningAssigned = assignedConverted;
        }
      } else {
        // No legal token found - use the converted message as-is
        beginningAssigned = assignedConverted
          .replace(
            /@\[marleyOptInDisabledLegalMessage\]\(marleyOptInDisabledLegalMessage\)/g,
            '',
          )
          .replace(/\$marleyOptInDisabledLegalMessage/g, '');
      }
    }

    // Parse unassigned message
    // The legacy token name is in the tokens array, not in the raw body
    const unassignedTokens = unassignedRule?.messageTemplate?.tokens || [];
    const unassignedRawBody = unassignedRule?.messageTemplate?.body || '';

    let beginningUnassigned = '';
    let endingUnassigned = '';

    // First, convert ALL tokens to mention format
    let unassignedConverted = unassignedRawBody;
    if (unassignedTokens.length > 0) {
      unassignedConverted = placeholderToTokens(
        unassignedRawBody,
        unassignedTokens,
      );
    }

    // Check if the LAST token is a legacy token with suffix
    // (e.g., marleyOptInDisabledLegalMessageBoo)
    const lastTokenUnassigned = unassignedTokens[unassignedTokens.length - 1];
    const legacyTokenMatchUnassigned = lastTokenUnassigned?.token?.match(
      /^marleyOptInDisabledLegalMessage(.+)$/,
    );

    if (legacyTokenMatchUnassigned) {
      // Legacy token found - extract suffix and remove the token from the message
      const [, suffix] = legacyTokenMatchUnassigned;
      const legacyTokenPattern = `@[${lastTokenUnassigned.token}](${lastTokenUnassigned.token})`;
      const lastTokenIndex =
        unassignedConverted.lastIndexOf(legacyTokenPattern);

      if (lastTokenIndex !== -1) {
        beginningUnassigned = unassignedConverted.substring(0, lastTokenIndex);
        endingUnassigned = suffix;
      } else {
        beginningUnassigned = unassignedConverted;
      }
    } else {
      // Standard format - split on legal message token if present
      const legalTokenPattern =
        /@\[marleyOptInDisabledLegalMessage\]\(marleyOptInDisabledLegalMessage\)|\$marleyOptInDisabledLegalMessage/;
      const legalTokenIndex = unassignedConverted.search(legalTokenPattern);

      if (legalTokenIndex !== -1) {
        // Legal token found - split the message
        const match = unassignedConverted.match(legalTokenPattern);
        if (match) {
          const parts = unassignedConverted.split(match[0]);
          beginningUnassigned = parts[0];
          endingUnassigned = parts.slice(1).join('');
          // Remove any remaining legal token occurrences from beginning
          beginningUnassigned = beginningUnassigned
            .replace(
              /@\[marleyOptInDisabledLegalMessage\]\(marleyOptInDisabledLegalMessage\)/g,
              '',
            )
            .replace(/\$marleyOptInDisabledLegalMessage/g, '');
        } else {
          beginningUnassigned = unassignedConverted;
        }
      } else {
        // No legal token found - use the converted message as-is
        beginningUnassigned = unassignedConverted
          .replace(
            /@\[marleyOptInDisabledLegalMessage\]\(marleyOptInDisabledLegalMessage\)/g,
            '',
          )
          .replace(/\$marleyOptInDisabledLegalMessage/g, '');
      }
    }

    return {
      beginningMessageAssigned: removeLastWhitespace(beginningAssigned),
      endingMessageAssigned: removeFirstWhitespace(endingAssigned),
      isEndingMessageEnabledAssigned: !!endingAssigned.trim(),
      beginningMessageUnassigned: removeLastWhitespace(beginningUnassigned),
      endingMessageUnassigned: removeFirstWhitespace(endingUnassigned),
      isEndingMessageEnabledUnassigned: !!endingUnassigned.trim(),
      followupMessage: followupBody,
      isFollowupEnabled: followupRule?.enabled ?? false,
      followupDelay:
        (followupRule?.rule?.event?.params?.delayInMs ??
          DEFAULT_FOLLOWUP_DELAY_MS_FOR_NEW_MS) / FOLLOWUP_DELAY_MINUTES_TO_MS,
    };
  }, [messageData, followupMessageData, findRuleWithLogging]);

  const methods = useForm<WelcomeMessageFormData>({
    defaultValues: savedData,
  });

  const {
    watch,
    reset,
    setValue,
    formState: { isDirty },
  } = methods;
  const currentValues = watch();

  // State for segmented control to toggle between assigned/unassigned preview
  const [messageView, setMessageView] = useState<'assigned' | 'unassigned'>(
    'assigned',
  );

  // Exclude the marleyOptInDisabledLegalMessage token from all message inputs
  // (it's automatically inserted between beginning and ending by the component)
  const ALLOWED_TOKENS = useMemo(
    () =>
      WELCOME_MESSAGE_TOKENS.filter(
        (token) => token.id !== 'marleyOptInDisabledLegalMessage',
      ),
    [],
  );

  // Only reset on freshly fetched data — otherwise a failed save discards the edits.
  const lastResetData = useRef<typeof savedData | null>(null);

  useEffect(() => {
    if (isSavingState || isFetchingData) return;
    if (lastResetData.current === savedData) return;
    lastResetData.current = savedData;
    reset(savedData);
  }, [savedData, reset, isSavingState, isFetchingData]);

  const handleSave = async () => {
    const legalToken = `@[${MOID_LEGAL_MESSAGE_TOKEN_ID}](${MOID_LEGAL_MESSAGE_TOKEN_ID})`;

    // Build assigned message
    const fullMessageAssigned = currentValues.isEndingMessageEnabledAssigned
      ? `${currentValues.beginningMessageAssigned} ${legalToken} ${currentValues.endingMessageAssigned}`
      : `${currentValues.beginningMessageAssigned} ${legalToken}`;

    const { body: messageBodyAssigned, tokens: messageTokensAssigned } =
      extractTokensWithDuplicates(fullMessageAssigned);

    // Build unassigned message
    const fullMessageUnassigned = currentValues.isEndingMessageEnabledUnassigned
      ? `${currentValues.beginningMessageUnassigned} ${legalToken} ${currentValues.endingMessageUnassigned}`
      : `${currentValues.beginningMessageUnassigned} ${legalToken}`;

    const { body: messageBodyUnassigned, tokens: messageTokensUnassigned } =
      extractTokensWithDuplicates(fullMessageUnassigned);

    const assignedRule = findRuleWithLogging(messageData, 'assigned');
    const unassignedRule = findRuleWithLogging(messageData, 'unassigned');
    const followupRule = findRuleWithLogging(followupMessageData, undefined);

    // Build assigned message rule
    const assignedRuleData: WelcomeMessageRule = buildRule({
      caseType,
      assignment: 'assigned',
      marleyOptInDisabled: true,
      body: messageBodyAssigned,
      tokens: messageTokensAssigned,
      existingRule: assignedRule,
    });

    // Build unassigned message rule (different message content)
    const unassignedRuleData: WelcomeMessageRule = buildRule({
      caseType,
      assignment: 'unassigned',
      marleyOptInDisabled: true,
      body: messageBodyUnassigned,
      tokens: messageTokensUnassigned,
      existingRule: unassignedRule,
    });

    // Replace in-place within the full ruleset to maintain original length
    const baseRules = (messageData?.rules || []) as WelcomeMessageRule[];
    let assignedFound = false;
    let unassignedFound = false;
    const rules: WelcomeMessageRule[] = baseRules.map((r) => {
      const conds = r.rule.conditions.all;
      const rCaseType = conds.find((c) => c.fact === 'caseType')?.value;
      const rMoid =
        conds.find((c) => c.fact === 'marleyOptInDisabled')?.value === true;
      const rAssign = conds.find((c) => c.fact === 'caseAssignment')?.value;
      if (rCaseType === caseType && rMoid && rAssign === true) {
        assignedFound = true;
        return assignedRuleData;
      }
      if (rCaseType === caseType && rMoid && rAssign === false) {
        unassignedFound = true;
        return unassignedRuleData;
      }
      return r;
    });
    if (!assignedFound) rules.push(assignedRuleData);
    if (!unassignedFound) rules.push(unassignedRuleData);

    // Ensure all rules include required fields (e.g., category)
    const rulesToSave = rules.map((r) =>
      cleanRule(r, 'WELCOME_FLOW_INITIAL_MESSAGE'),
    );

    const isFollowupSent =
      currentValues.isFollowupEnabled &&
      !!currentValues.followupMessage?.trim();

    // '' and undefined both fail the create path; `enabled` stops the send instead.
    const followupBody = isFollowupSent
      ? currentValues.followupMessage
      : savedData.followupMessage;

    const followupContent = followupBody?.trim()
      ? extractTokensWithDuplicates(followupBody)
      : undefined;

    const followupRuleData: WelcomeMessageRule = buildRule({
      caseType,
      marleyOptInDisabled: true,
      ...followupContent,
      existingRule: followupRule,
    });
    followupRuleData.enabled = isFollowupSent;
    if (followupRuleData?.rule?.event?.params) {
      followupRuleData.rule.event.params.delayInMs =
        currentValues.followupDelay * FOLLOWUP_DELAY_MINUTES_TO_MS;
    }

    // Include ALL existing rules from FOLLOWUP ruleset, except the one we're replacing
    const otherFollowupRules =
      followupMessageData?.rules?.filter((r) => r.id !== followupRule?.id) ||
      [];

    const followupRules: WelcomeMessageRule[] = [
      ...otherFollowupRules,
      followupRuleData,
    ];

    const followupDelayMs =
      currentValues.followupDelay * FOLLOWUP_DELAY_MINUTES_TO_MS;
    const followupRulesToSave = followupRules.map((r) =>
      cleanRule(r, 'WELCOME_FLOW_OPTIN_FOLLOWUP', followupDelayMs),
    );

    // Save ruleset
    setIsSavingState(true);
    try {
      await saveMultipleRulesets([
        {
          ruleset: {
            id: messageData?.id,
            rules: rulesToSave,
            lineOfBusinessId: messageData?.lineOfBusinessId,
          },
          rulesetType: rulesetTypeFor(
            'WELCOME_FLOW_INITIAL_MESSAGE',
            direction,
          ),
          lineOfBusinessId,
        },
        {
          ruleset: {
            id: followupMessageData?.id,
            rules: followupRulesToSave,
            lineOfBusinessId: followupMessageData?.lineOfBusinessId,
          },
          rulesetType: rulesetTypeFor('WELCOME_FLOW_OPTIN_FOLLOWUP', direction),
          lineOfBusinessId,
        },
      ]);

      // Show success toast
      toaster.create({
        title: t('marleyOptInDisabled.saveSuccess'),
        type: 'success',
      });

      try {
        await invalidateWelcomeMessageQueries(
          queryClient,
          lineOfBusinessId,
          direction,
        );
      } catch {
        toaster.create({
          title: t('marleyOptInDisabled.savedButCouldNotReload'),
          description: t('shared.savedButCouldNotReloadDescription'),
          type: 'warning',
        });
      }
    } catch (error) {
      // Show error toast
      toaster.create({
        title: t('marleyOptInDisabled.saveError'),
        description:
          error instanceof Error ? error.message : t('shared.unknownError'),
        type: 'error',
      });

      // Don't throw - keep form dirty so user can retry
    } finally {
      // Form will be reset automatically by useEffect when fresh savedData arrives
      // Save button stays disabled during refetch via isFetchingData
      setIsSavingState(false);
    }
  };

  const handleCancel = () => {
    reset();
  };

  // Build preview messages with mock token values
  const messages: MessageItem[] = [
    {
      id: '1',
      type: 'isOutgoing',
      message:
        messageView === 'assigned'
          ? replaceTokensWithPreviewValues(
              `${convertMentionFormatToPlainTokens(currentValues.beginningMessageAssigned)} $marleyOptInDisabledLegalMessage${currentValues.isEndingMessageEnabledAssigned ? ` ${convertMentionFormatToPlainTokens(currentValues.endingMessageAssigned)}` : ''}`,
            )
          : replaceTokensWithPreviewValues(
              `${convertMentionFormatToPlainTokens(currentValues.beginningMessageUnassigned)} $marleyOptInDisabledLegalMessage${currentValues.isEndingMessageEnabledUnassigned ? ` ${convertMentionFormatToPlainTokens(currentValues.endingMessageUnassigned)}` : ''}`,
            ),
    },
  ];
  if (currentValues.isFollowupEnabled && currentValues.followupMessage) {
    messages.push({
      id: '2',
      type: 'isOutgoing',
      message: replaceTokensWithPreviewValues(
        convertMentionFormatToPlainTokens(currentValues.followupMessage),
      ),
    });
  }

  return (
    <FormProvider {...methods}>
      <LayoutAdminPage
        breadcrumbs
        isDirty={isDirty}
        header={
          <LayoutAdminPageHeader
            title={config?.title ?? ''}
            description={config?.description ?? ''}
            isEditing={isDirty && !isFetchingData}
            isSaving={isSavingState || isFetchingData}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        }
        childrenScroll
        dataTestId="welcome-message-marley-opt-in-disabled"
      >
        <LayoutAdminMessagePreview
          editHeader={{
            title: t('marleyOptInDisabled.configureTitle'),
            description: t('marleyOptInDisabled.configureDescription'),
          }}
          editContent={
            <Stack gap={6}>
              <Alert
                status="warning"
                title={t('marleyOptInDisabled.orgOptInAlertTitle')}
              >
                {t('marleyOptInDisabled.orgOptInAlertBody')}
              </Alert>

              <SegmentedControl
                value={messageView}
                onValueChange={(e) =>
                  setMessageView(e.value as 'assigned' | 'unassigned')
                }
                items={[
                  {
                    value: 'assigned',
                    label: t('marleyOptInDisabled.viewAssigned'),
                  },
                  {
                    value: 'unassigned',
                    label: t('marleyOptInDisabled.viewUnassigned'),
                  },
                ]}
                fitted
              />

              {messageView === 'assigned' ? (
                <>
                  <Field
                    label={t('welcomeNotOptedIn.beginningMessageLabel')}
                    helperText={t(
                      'marleyOptInDisabled.beginningHelperAssigned',
                    )}
                    invalid={
                      !!methods.formState.errors.beginningMessageAssigned
                    }
                    errorText={
                      methods.formState.errors.beginningMessageAssigned?.message
                    }
                    disabled={isLoadingData}
                  >
                    <TaggedTextarea
                      value={currentValues.beginningMessageAssigned}
                      onChange={(e) => {
                        // Normalize plain $tokens to mention format to ensure consistent token ordering
                        const normalized = convertTokensToMentionFormat(
                          e.target.value,
                        );
                        methods.setValue(
                          'beginningMessageAssigned',
                          normalized,
                          { shouldDirty: true, shouldValidate: true },
                        );
                      }}
                      trigger="$"
                      data={ALLOWED_TOKENS}
                      placeholder={t('shared.tokenPlaceholder')}
                      maxLength={WELCOME_BEGINNING_MAX_LENGTH}
                      showCharCount
                      testId="beginning-message-assigned-input"
                      displayTransform={tokenDisplayTransform}
                    />
                  </Field>

                  <Stack gap={0} justify="stretch" align="start">
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="fg.subtle"
                      userSelect="none"
                    >
                      {t('welcomeNotOptedIn.legalMessageLabel')}
                    </Text>
                    <Text
                      fontSize="xs"
                      p={3}
                      bg="bg.surface.lv2"
                      borderRadius="md"
                      userSelect="none"
                    >
                      {MOID_LEGAL_MESSAGE}
                    </Text>
                  </Stack>

                  <Stack gap={4}>
                    <Switch
                      checked={currentValues.isEndingMessageEnabledAssigned}
                      onCheckedChange={(details) =>
                        setValue(
                          'isEndingMessageEnabledAssigned',
                          details.checked,
                          { shouldDirty: true },
                        )
                      }
                      size="lg"
                      colorPalette="info"
                      disabled={isLoadingData}
                      data-testid="ending-message-enabled-switch-assigned"
                    >
                      <Stack gap={1}>
                        <Text fontSize="sm" fontWeight="medium">
                          {t('welcomeNotOptedIn.includeEndingMessage')}
                        </Text>
                        <Text fontSize="xs" color="fg.muted">
                          {t('welcomeNotOptedIn.includeEndingMessageHelper')}
                        </Text>
                      </Stack>
                    </Switch>

                    {currentValues.isEndingMessageEnabledAssigned && (
                      <Field
                        label={t('welcomeNotOptedIn.endingMessageLabel')}
                        helperText={t('welcomeNotOptedIn.endingMessageHelper')}
                        disabled={isLoadingData}
                      >
                        <TaggedTextarea
                          value={currentValues.endingMessageAssigned}
                          onChange={(e) => {
                            // Normalize plain $tokens to mention format to ensure consistent token ordering
                            const normalized = convertTokensToMentionFormat(
                              e.target.value,
                            );
                            methods.setValue(
                              'endingMessageAssigned',
                              normalized,
                              { shouldDirty: true },
                            );
                          }}
                          trigger="$"
                          data={ALLOWED_TOKENS}
                          placeholder={t('shared.tokenPlaceholder')}
                          maxLength={WELCOME_ENDING_MAX_LENGTH}
                          showCharCount
                          testId="ending-message-assigned-input"
                          displayTransform={tokenDisplayTransform}
                        />
                      </Field>
                    )}
                  </Stack>
                </>
              ) : (
                <>
                  <Field
                    label={t('welcomeNotOptedIn.beginningMessageLabel')}
                    helperText={t(
                      'marleyOptInDisabled.beginningHelperUnassigned',
                    )}
                    invalid={
                      !!methods.formState.errors.beginningMessageUnassigned
                    }
                    errorText={
                      methods.formState.errors.beginningMessageUnassigned
                        ?.message
                    }
                    disabled={isLoadingData}
                  >
                    <TaggedTextarea
                      value={currentValues.beginningMessageUnassigned}
                      onChange={(e) => {
                        // Normalize plain $tokens to mention format to ensure consistent token ordering
                        const normalized = convertTokensToMentionFormat(
                          e.target.value,
                        );
                        methods.setValue(
                          'beginningMessageUnassigned',
                          normalized,
                          { shouldDirty: true, shouldValidate: true },
                        );
                      }}
                      trigger="$"
                      data={ALLOWED_TOKENS}
                      placeholder={t('shared.tokenPlaceholder')}
                      maxLength={WELCOME_BEGINNING_MAX_LENGTH}
                      showCharCount
                      testId="beginning-message-unassigned-input"
                      displayTransform={tokenDisplayTransform}
                    />
                  </Field>

                  <Stack gap={2}>
                    <Text fontSize="sm" fontWeight="medium" color="fg.subtle">
                      {t('welcomeNotOptedIn.legalMessageLabel')}
                    </Text>
                    <Text
                      fontSize="sm"
                      color="fg.muted"
                      p={3}
                      bg="bg.muted"
                      borderRadius="md"
                    >
                      {MOID_LEGAL_MESSAGE}
                    </Text>
                  </Stack>

                  <Stack gap={4}>
                    <Switch
                      checked={currentValues.isEndingMessageEnabledUnassigned}
                      onCheckedChange={(details) =>
                        setValue(
                          'isEndingMessageEnabledUnassigned',
                          details.checked,
                          { shouldDirty: true },
                        )
                      }
                      size="lg"
                      colorPalette="info"
                      disabled={isLoadingData}
                      data-testid="ending-message-enabled-switch-unassigned"
                    >
                      <Stack gap={1}>
                        <Text fontSize="sm" fontWeight="medium">
                          {t('welcomeNotOptedIn.includeEndingMessage')}
                        </Text>
                        <Text fontSize="xs" color="fg.muted">
                          {t('welcomeNotOptedIn.includeEndingMessageHelper')}
                        </Text>
                      </Stack>
                    </Switch>

                    {currentValues.isEndingMessageEnabledUnassigned && (
                      <Field
                        label={t('welcomeNotOptedIn.endingMessageLabel')}
                        helperText={t('welcomeNotOptedIn.endingMessageHelper')}
                        disabled={isLoadingData}
                      >
                        <TaggedTextarea
                          value={currentValues.endingMessageUnassigned}
                          onChange={(e) => {
                            // Normalize plain $tokens to mention format to ensure consistent token ordering
                            const normalized = convertTokensToMentionFormat(
                              e.target.value,
                            );
                            methods.setValue(
                              'endingMessageUnassigned',
                              normalized,
                              { shouldDirty: true },
                            );
                          }}
                          trigger="$"
                          data={ALLOWED_TOKENS}
                          placeholder={t('shared.tokenPlaceholder')}
                          maxLength={WELCOME_ENDING_MAX_LENGTH}
                          showCharCount
                          testId="ending-message-unassigned-input"
                          displayTransform={tokenDisplayTransform}
                        />
                      </Field>
                    )}
                  </Stack>
                </>
              )}
              <Stack gap={4}>
                <Switch
                  checked={currentValues.isFollowupEnabled}
                  onCheckedChange={(details) =>
                    setValue('isFollowupEnabled', details.checked, {
                      shouldDirty: true,
                    })
                  }
                  size="lg"
                  colorPalette="info"
                  disabled={isLoadingData}
                >
                  <Stack gap={1}>
                    <Text fontSize="sm" fontWeight="medium">
                      {t('welcomeNotOptedIn.optionalFollowUp')}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      {t('welcomeNotOptedIn.optionalFollowUpHelper')}
                    </Text>
                  </Stack>
                </Switch>
                {currentValues.isFollowupEnabled && (
                  <>
                    <Field
                      label={t('welcomeNotOptedIn.followUpDelayLabel')}
                      helperText={t('welcomeNotOptedIn.followUpDelayHelper')}
                      disabled={isLoadingData}
                    >
                      <NumberInputRoot
                        value={`${currentValues.followupDelay}`}
                        min={MIN_FOLLOWUP_DELAY_MINUTES}
                        max={MAX_FOLLOWUP_DELAY_MINUTES}
                        step={1}
                        onValueChange={(details) =>
                          setValue(
                            'followupDelay',
                            parseInt(details.value || '0', 10) || 0,
                            {
                              shouldDirty: true,
                            },
                          )
                        }
                        maxW="30%"
                        withControl
                      >
                        <NumberInputField data-testid="followup-delay-input" />
                      </NumberInputRoot>
                    </Field>
                    <Field
                      label={t('welcomeNotOptedIn.followUpMessageLabel')}
                      disabled={isLoadingData}
                    >
                      <TaggedTextarea
                        value={currentValues.followupMessage}
                        onChange={(e) => {
                          // Normalize plain $tokens to mention format to ensure consistent token ordering
                          const normalized = convertTokensToMentionFormat(
                            e.target.value,
                          );
                          methods.setValue('followupMessage', normalized, {
                            shouldDirty: true,
                          });
                        }}
                        trigger="$"
                        data={WELCOME_MESSAGE_TOKENS}
                        placeholder={t('shared.tokenPlaceholder')}
                        maxLength={WELCOME_CONFIRMATION_MAX_LENGTH}
                        showCharCount
                        testId="followup-message-input"
                        displayTransform={tokenDisplayTransform}
                      />
                    </Field>
                  </>
                )}
              </Stack>
              <Alert status="info" title={t('shared.tokenAlertShort')} />
            </Stack>
          }
          previewTitle={t('shared.previewTitle')}
          previewDescription={
            messageView === 'assigned'
              ? t('marleyOptInDisabled.previewDescriptionAssigned')
              : t('marleyOptInDisabled.previewDescriptionUnassigned')
          }
          messages={messages}
          orgPrefix={orgPrefix}
          pointOfView="contact"
        />
      </LayoutAdminPage>
    </FormProvider>
  );
};

export default WelcomeMessageMarleyOptInDisabled;
